import { useEffect } from "react";
import ClearwaterHeroSection from "./components/HeroSection";
import ClearwaterStickyNav from "./components/StickyNav";
import ClearwaterProcessingTimeSection from "./components/ProcessingTimeSection";
import ClearwaterServicesSection from "./components/ServicesSection";
import ClearwaterLocalAreaSection from "./components/LocalAreaSection";
import ClearwaterTravelTimingSection from "./components/TravelTimingSection";
import ClearwaterDestinationsSection from "./components/DestinationsSection";
import ClearwaterTestimonialsSection from "./components/TestimonialsSection";
import ClearwaterAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import ClearwaterComparisonSection from "./components/ComparisonSection";
import ClearwaterMistakesSection from "./components/MistakesSection";
import ClearwaterHowItWorksSection from "./components/HowItWorksSection";
import ClearwaterFAQSection, { faqs } from "./components/FAQSection";
import ClearwaterHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import ClearwaterFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function ClearwaterPage() {
  useEffect(() => {
    document.title = "Emergency Passport Clearwater, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/clearwater/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Clearwater, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Pinellas County travelers, Tampa Bay cruise passengers, and spring break families.");
    setMeta("geo.placename", "Clearwater, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "27.9659;-82.8001");
    setMeta("ICBM", "27.9659, -82.8001");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Clearwater Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Clearwater — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Clearwater and Pinellas County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Clearwater",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Clearwater",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <ClearwaterHeroSection />
      <ClearwaterStickyNav />
      <ClearwaterProcessingTimeSection />
      <ClearwaterServicesSection />
      <ClearwaterLocalAreaSection />
      <ClearwaterTravelTimingSection />
      <ClearwaterDestinationsSection />
      <ClearwaterTestimonialsSection />
      <ClearwaterAcceptanceFacilitiesSection />
      <ClearwaterComparisonSection />
      <ClearwaterMistakesSection />
      <ClearwaterHowItWorksSection />
      <ClearwaterFAQSection />
      <ClearwaterHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/clearwater" state="FL" />
      <ClearwaterFinalCTA />
    </div>
  );
}